import { type FC, type KeyboardEvent, useState } from 'react'
import { Badge, Form } from 'react-bootstrap'
import { Button } from '../../../../../../../components/Button/Button'
import { DateField, StartWorkflowActions, type StartWorkflowFormProps } from '../StartWorkflowFields'
import { wealthboxWorkflowAdapter } from './wealthboxWorkflowAdapter'

interface LabelFieldProps {
  value: string
  onChange: (label: string) => void
}

const LabelField: FC<LabelFieldProps> = ({ value, onChange }) => {
  const [draft, setDraft] = useState<string | null>(null)

  const commit = () => {
    onChange(draft?.trim() ?? value)
    setDraft(null)
  }

  const handleKeyDown = (event: KeyboardEvent<HTMLInputElement>) => {
    if (event.key === 'Enter') {
      event.preventDefault()
      commit()
    }
    if (event.key === 'Escape') {
      event.stopPropagation()
      setDraft(null)
    }
  }

  if (draft !== null) {
    return (
      <Form.Control
        autoFocus
        size='sm'
        className='w-auto'
        aria-label='Label'
        placeholder='Add label'
        value={draft}
        onChange={(event) => setDraft(event.target.value)}
        onBlur={commit}
        onKeyDown={handleKeyDown}
      />
    )
  }

  return value ? (
    <Badge bg='' className='zocks-tag-slate d-inline-flex align-items-center gap-1 paragraph-small'>
      {value}
      <button type='button' className='btn p-0 border-0 lh-1 text-reset' aria-label={`Remove label ${value}`} onClick={() => onChange('')}>
        <i className='bi bi-x' />
      </button>
    </Badge>
  ) : (
    <Button variant='dashedButton' className='button-small' onClick={() => setDraft('')}>
      <i className='bi bi-plus-lg' />
      Add label
    </Button>
  )
}

export const WealthboxStartWorkflowForm: FC<StartWorkflowFormProps> = ({ target, template, suggestedLabel, onStarted }) => {
  const [label, setLabel] = useState(suggestedLabel ?? '')
  const [startsAt, setStartsAt] = useState<Date | null>(null)
  const [milestoneDates, setMilestoneDates] = useState<Record<string, Date | null>>({})

  const milestones = template.milestones.flatMap((milestone) => {
    const date = milestoneDates[milestone.id]
    return date ? [{ id: milestone.id, date }] : []
  })
  const buildPayload =
    milestones.length === template.milestones.length
      ? () =>
          wealthboxWorkflowAdapter.buildStartPayload(
            { templateId: template.id, label: label || undefined, startsAt: startsAt ?? new Date(), milestones },
            target.externalId,
          )
      : null

  return (
    <div className='d-flex flex-column gap-3'>
      {template.description ? (
        <div>
          <Form.Label>Description</Form.Label>
          <p className='paragraph-small zocks-text-gray-600 mb-0'>{template.description}</p>
        </div>
      ) : null}
      <div className='d-flex flex-column align-items-start'>
        <Form.Label>Label</Form.Label>
        <LabelField value={label} onChange={setLabel} />
      </div>
      <div className='d-flex flex-column align-items-start'>
        <Form.Label>
          Start date <span className='zocks-text-gray-600'>(Optional)</span>
        </Form.Label>
        <DateField label='Start date' value={startsAt} placeholder='Pick a starting date' onChange={setStartsAt} />
      </div>
      {template.milestones.map((milestone) => (
        <div key={milestone.id} className='d-flex flex-column align-items-start'>
          <Form.Label>{milestone.name}</Form.Label>
          <DateField
            label={`${milestone.name ?? 'Milestone'} date`}
            value={milestoneDates[milestone.id] ?? null}
            placeholder='Pick a milestone date'
            onChange={(date) => setMilestoneDates((dates) => ({ ...dates, [milestone.id]: date }))}
          />
        </div>
      ))}
      <StartWorkflowActions target={target} templateId={template.id} buildPayload={buildPayload} onStarted={onStarted} />
    </div>
  )
}
